import { randomUUID } from "@bindery/logging/random-uuid";
import * as z from "zod";
import { storageKey } from "@/site";

// Russ, 2026-10-04 (#277): a session spans readings on one device and
// ends after 30 minutes without activity.
export const READING_SESSION_IDLE_MS = 30 * 60 * 1000;

// A clock set back further than this (a corrected clock, a time-zone or
// manual change) ends the session: otherwise the idle gap is measured
// from a future lastActivityAt and one session can stretch over days.
export const READING_SESSION_CLOCK_SKEW_MS = 60 * 1000;

// Keyed per reader, so a reader switch on a shared device can never hand
// one reader's session to the next.
export const readingSessionKey = (readerId: string): string =>
  storageKey(`readingSession.v1:${readerId}`);

/** The `sessionId` and `sessionStartTime` a record or response carries. */
export interface ReadingSessionStamp {
  sessionId: string;
  sessionStartTime: number;
}

const storedSession = z.object({
  sessionId: z.string().min(1).max(128),
  sessionStartTime: z.number(),
  lastActivityAt: z.number(),
});
type StoredSession = z.infer<typeof storedSession>;

type SessionStorage = Pick<Storage, "getItem" | "setItem">;

export interface ReadingSessionClockDeps {
  storage?: SessionStorage | null;
  now?: () => number;
  newId?: () => string;
}

/**
 * One reader's reading session on this device. Activity is opening a
 * reading, building a learning record or queueing a fluency response; a
 * session with no activity for READING_SESSION_IDLE_MS is over, and the
 * next activity starts another. Persisted in localStorage, so it
 * survives page navigations and reloads and is shared by this device's tabs.
 */
export interface ReadingSessionClock {
  readonly readerId: string;
  /** The live session, starting one if none is; not activity itself. */
  current: () => ReadingSessionStamp;
  /** Records activity now and returns the session it belongs to. */
  activity: () => ReadingSessionStamp;
}

const browserStorage = (): SessionStorage | null => {
  try {
    return localStorage;
  } catch {
    return null;
  }
};

const isLive = (session: StoredSession, now: number): boolean => {
  const idle = now - session.lastActivityAt;
  return (
    idle > -READING_SESSION_CLOCK_SKEW_MS && idle < READING_SESSION_IDLE_MS
  );
};

const stampOf = ({
  sessionId,
  sessionStartTime,
}: StoredSession): ReadingSessionStamp => ({ sessionId, sessionStartTime });

export function createReadingSessionClock(
  readerId: string,
  {
    storage = browserStorage(),
    now = Date.now,
    newId = randomUUID,
  }: ReadingSessionClockDeps = {},
): ReadingSessionClock {
  const key = readingSessionKey(readerId);
  // Safari's private mode throws on setItem; the session then lasts as
  // long as this clock does, rather than restarting on every record.
  let inMemory: StoredSession | null = null;
  let persists = storage !== null;

  function read(): StoredSession | null {
    if (storage === null || !persists) return inMemory;
    try {
      const raw = storage.getItem(key);
      if (raw === null) return null;
      const parsed = storedSession.safeParse(JSON.parse(raw));
      return parsed.success ? parsed.data : null;
    } catch {
      return inMemory;
    }
  }

  function write(session: StoredSession): StoredSession {
    inMemory = session;
    try {
      storage?.setItem(key, JSON.stringify(session));
    } catch {
      persists = false;
    }
    return session;
  }

  function live(at: number): StoredSession {
    const stored = read();
    if (stored !== null && isLive(stored, at)) return stored;
    return write({
      sessionId: newId(),
      sessionStartTime: at,
      lastActivityAt: at,
    });
  }

  return {
    readerId,
    current: () => stampOf(live(now())),
    activity: () => {
      const at = now();
      const session = live(at);
      return stampOf(
        write({
          ...session,
          lastActivityAt: Math.max(session.lastActivityAt, at),
        }),
      );
    },
  };
}
